import { useCallback, useEffect, useState } from "react";

export type PdfZoomScope = "identify" | "extract" | "match";

const STORAGE_KEY_PREFIX = "returns_pdf_zoom_percent_";
const DEFAULT_ZOOM = 100;
const MIN_ZOOM = 50;
const MAX_ZOOM = 300;
const STEP = 25;

const clamp = (value: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));

const storageKeyFor = (scope: PdfZoomScope) => `${STORAGE_KEY_PREFIX}${scope}`;

const readStoredZoom = (scope: PdfZoomScope): number => {
    try {
        const raw = localStorage.getItem(storageKeyFor(scope));
        if (!raw) return DEFAULT_ZOOM;
        const parsed = parseInt(raw, 10);
        if (Number.isNaN(parsed)) return DEFAULT_ZOOM;
        return clamp(parsed);
    } catch {
        return DEFAULT_ZOOM;
    }
};

const usePdfZoom = (scope: PdfZoomScope) => {
    const [zoomPercent, setZoomPercentState] = useState<number>(() => readStoredZoom(scope));

    useEffect(() => {
        try {
            localStorage.setItem(storageKeyFor(scope), String(zoomPercent));
        } catch (error) {
            console.error("Failed to persist PDF zoom:", error);
        }
    }, [scope, zoomPercent]);

    const setZoomPercent = useCallback((value: number) => {
        setZoomPercentState(clamp(Math.round(value)));
    }, []);

    const zoomIn = useCallback(() => {
        setZoomPercentState((prev) => clamp(prev + STEP));
    }, []);

    const zoomOut = useCallback(() => {
        setZoomPercentState((prev) => clamp(prev - STEP));
    }, []);

    const reset = useCallback(() => {
        setZoomPercentState(DEFAULT_ZOOM);
    }, []);

    return {
        zoomPercent,
        setZoomPercent,
        zoomIn,
        zoomOut,
        reset,
        canZoomIn: zoomPercent < MAX_ZOOM,
        canZoomOut: zoomPercent > MIN_ZOOM,
        isDefault: zoomPercent === DEFAULT_ZOOM,
    };
};

export default usePdfZoom;
